import { Icon } from '@iconify/react';
import closeIcon from '@iconify/icons-material-symbols/close-rounded';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/Dialog';

interface DownloadReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDownloadCSV: () => void;
  onDownloadPDF: () => void;
  // Optional - lets a caller whose PDF-specific data failed to load (e.g. a background query
  // error) disable just the PDF option while CSV export stays available. Omitted/false by
  // default so existing callers are unaffected.
  pdfDisabled?: boolean;
  pdfDisabledMessage?: string;
  // Optional - a failed PDF attempt, shown in the dialog so it's next to the retry rather than on the page.
  errorMessage?: string;
}

const optionButtonClasses =
  "flex w-full items-center justify-center gap-2 rounded-md border-2 border-[#00afd8] bg-white px-5 py-3 font-['GT-Walsheim-Pro-Bold'] uppercase tracking-[0.02em] text-[#00afd8] transition-all duration-200 ease-in-out hover:-translate-y-0.5 hover:bg-[#e6f9fd] hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#009fc4] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:translate-y-0 disabled:hover:shadow-none";

const DownloadReportModal = ({
  isOpen,
  onClose,
  onDownloadCSV,
  onDownloadPDF,
  pdfDisabled,
  pdfDisabledMessage,
  errorMessage,
}: DownloadReportModalProps) => {
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className='w-[min(92vw,34rem)]'>
        <DialogClose asChild>
          <button
            type='button'
            aria-label='Close'
            className='absolute right-[1rem] top-[1rem] flex h-[3rem] w-[3rem] items-center justify-center rounded-full border-0 bg-[#e5e5e5] p-[0.5rem] transition-colors hover:bg-[#d0d0d0]'
          >
            <Icon className='h-[2rem] w-[2rem] cursor-pointer' icon={closeIcon} />
          </button>
        </DialogClose>
        <DialogHeader>
          <DialogTitle className='text-4xl font-bold text-slate-900'>Download Report</DialogTitle>
          {/* Dialog's own default `text-sm` class wins the cascade over a plain `text-3xl` override
              here (Tailwind's compiled utility order doesn't guarantee a later-used class wins),
              so the size needs the `!` important-modifier to actually take effect. */}
          <DialogDescription className='!text-3xl text-slate-600'>Download the report as a:</DialogDescription>
        </DialogHeader>
        <div className='mt-2 grid grid-cols-2 gap-4'>
          <button
            type='button'
            className={optionButtonClasses}
            onClick={onDownloadPDF}
            disabled={pdfDisabled}
            title={pdfDisabled ? pdfDisabledMessage : undefined}
          >
            <Icon icon='mdi:file-pdf-box' width={40} height={40} className='text-[#e0403a]' />
            <span className='text-2xl'>PDF file</span>
          </button>
          <button type='button' className={optionButtonClasses} onClick={onDownloadCSV}>
            <Icon icon='mdi:file-excel-outline' width={40} height={40} className='text-[#217346]' />
            <span className='text-2xl'>Excel file</span>
          </button>
        </div>
        {pdfDisabled && pdfDisabledMessage && (
          <p className='mt-2 text-center text-sm text-red-600'>{pdfDisabledMessage}</p>
        )}
        {errorMessage && <p className='mt-2 text-center text-sm text-red-600'>{errorMessage}</p>}
      </DialogContent>
    </Dialog>
  );
};

export default DownloadReportModal;
